/**
 * FormationCanvas - Formation editor wrapper around BaseCanvas
 * Simplified canvas for player positioning only:
 * - Player operations enabled (add, move, delete, label, color)
 * - Drawing operations disabled (no routes/motions)
 * - Default 5 linemen initialization
 * - Position extraction for saving formations
 */

import { useEffect, useRef, useState } from 'react'
import { BaseCanvas } from './BaseCanvas'
import { CanvasDialogs } from './CanvasDialogs'
import { useCanvasCallbacks, useStateRef } from './hooks'
import { createDefaultLinemen } from '@/utils/Lineman.utils'
import type { Player } from '@/types'
import type { DrawingState, HashAlignment } from '@/types'

interface FormationCanvasProps {
	hashAlignment: HashAlignment
	initialPositions?: FormationPosition[]
	onCanvasStateReady?: (getState: () => { players: Player[] }) => void
	readonly?: boolean
	// Hash mode system - external control
	externalPlayers?: Player[]
	drawingState?: DrawingState
	onPlayersChange?: (players: Player[]) => void
}

export interface FormationPosition {
	x: number
	y: number
	label: string | null
	color: string | null
}

// Fixed drawing state - all drawing operations disabled
const FORMATION_DRAWING_STATE: DrawingState = {
	tool: 'select',
	color: '#000000',
	brushSize: 7,
	lineStyle: 'solid',
	lineEnd: 'none',
	pathMode: 'sharp',
	eraseSize: 40,
	snapThreshold: 20,
}

/**
 * FormationCanvas Component
 * Wraps BaseCanvas with local state for formation editing (player positions only)
 */
export function FormationCanvas({
									hashAlignment,
									initialPositions,
									onCanvasStateReady,
									readonly = false,
									externalPlayers,
									drawingState,
									onPlayersChange,
								}: FormationCanvasProps) {
	// Local state - players only
	const [players, setPlayers] = useState<Player[]>(() => {
		if (externalPlayers) return externalPlayers
		return createDefaultLinemen(hashAlignment)
	})
	const [isInitialized, setIsInitialized] = useState(false)
	const prevHashAlignmentRef = useRef<HashAlignment>(hashAlignment)
	const hasInitializedExternalRef = useRef(false)
	const isSyncingFromExternalRef = useRef(false)

	// Load initial positions if provided (edit mode - non-hash mode)
	useEffect(() => {
		if (!isInitialized && initialPositions && initialPositions.length > 0) {
			const reconstructed = reconstructFromPositions(initialPositions)
			setPlayers(reconstructed)
			setIsInitialized(true)
		}
	}, [initialPositions, isInitialized])

	// Sync external players to internal state
	// Always syncs when externalPlayers changes (not just on init/hash change)
	useEffect(() => {
		if (!externalPlayers || externalPlayers.length === 0) return

		// Mark that we're syncing FROM external to prevent echo
		isSyncingFromExternalRef.current = true
		setPlayers(externalPlayers.map(p => ({ ...p })))
		prevHashAlignmentRef.current = hashAlignment
		hasInitializedExternalRef.current = true
	}, [externalPlayers, hashAlignment])

	// Notify parent when players change
	// Skip notification if this update came FROM the parent (echo prevention)
	useEffect(() => {
		if (isSyncingFromExternalRef.current) {
			isSyncingFromExternalRef.current = false
			return
		}
		onPlayersChange?.(players)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [players]) // Intentionally omit onPlayersChange to prevent infinite loop

	// Provide getState function to parent for on-demand state access
	useStateRef({ players }, onCanvasStateReady)

	// Build callbacks for local state management (player operations only)
	const callbacks = useCanvasCallbacks({
		setDrawings: () => {
		},
		setPlayers,
		disableDrawings: true,
	})

	// Render dialogs via render prop
	function renderDialogs(state: any, actions: any) {
		return (
			<CanvasDialogs
				state={state}
				actions={actions}
				players={players}
				drawings={[]} // Formations never have drawings
				callbacks={callbacks}
				showDrawingEditor={false} // No drawing editor for formations
			/>
		)
	}

	return (
		<BaseCanvas
			drawingState={drawingState ?? FORMATION_DRAWING_STATE}
			drawings={[]} // Always empty - no drawings for formations
			players={players}
			callbacks={callbacks}
			hashAlignment={hashAlignment}
			containerMode="fill"
			showFieldMarkings={true}
			showPlayBar={false}
			readonly={readonly}
			renderDialogs={renderDialogs}
		/>
	)
}

/**
 * Extract player positions from canvas state for saving
 */
export function extractFormationPositions(players: Player[]): FormationPosition[] {
	return players.map((p) => ({
		x: p.x,
		y: p.y,
		label: p.label || null,
		color: p.color || null,
	}))
}

// Lineman labels for isLineman derivation
const LINEMAN_LABELS = new Set(['lt', 'lg', 'c', 'rg', 'rt'])

// Default colors
const DEFAULT_LINEMAN_COLOR = '#FFFFFF'
const DEFAULT_SKILL_PLAYER_COLOR = '#3b82f6'

/**
 * Reconstruct players from saved positions
 */
export function reconstructFromPositions(
	positions: FormationPosition[],
): Player[] {
	if (!positions || positions.length === 0) {
		return []
	}

	return positions.map((pos) => {
		const labelLower = (pos.label || '').toLowerCase()
		const isLineman = LINEMAN_LABELS.has(labelLower)
		return {
			id: crypto.randomUUID(),
			x: pos.x,
			y: pos.y,
			label: pos.label || '',
			color: pos.color || (isLineman ? DEFAULT_LINEMAN_COLOR : DEFAULT_SKILL_PLAYER_COLOR),
			isLineman,
		}
	})
}
